
import { required, minLength, email } from 'vuelidate/lib/validators'
import { validationMixin } from 'vuelidate'
import axios from 'axios'
import { metaDataMixin } from '~/mixins'

export default {
  mixins: [
    validationMixin,
    metaDataMixin,
  ],
  layout: 'careers',
  async asyncData ({ $api, $config, params: { slug } = {}, route, store, redirect }) {
    // Get the form elements of the vacancy
    const dynamicInputs = {}
    const {
      data: {
        item: {
          dynamicFields,
          defaultFields: { phone, portraitPhoto, coverLetter, cv } = {},
          social,
          ...vacancy
        } = {},
      } = {},
      status = 500,
    } = await $api.recruitment.getSingleVacancy(slug)

    if (status !== 200) {
      return redirect(301, '/werken-bij/vacatures')
    }

    if (dynamicFields) {
      dynamicFields.forEach((f) => {
        dynamicInputs[`input${f.id}`] = null
        if (f.kind === 'file') {
          dynamicInputs[`input${f.id}File`] = null
        }
      })
    }

    // Get recruiter for vacancies
    const {
      data: {
        item: recruiter = {},
      } = {},
    } = await $api.persons.getOne($config.careers.vacancies.recruiterId)

    // Set breadcrumbs
    store.dispatch('breadcrumbs/set', {
      path: route.fullPath,
      floating: true,
      width: 10,
      items: [
        {
          to: '/werken-bij',
          label: 'Werken bij',
        },
        {
          to: '/werken-bij/vacatures',
          label: 'Vacatures',
        },
        {
          label: vacancy.title,
        },
      ],
    })

    return {
      ...vacancy,
      dynamicFields,
      phone,
      portraitPhoto,
      coverLetter,
      cv,
      recruiter,
      ...(social
        ? {
            seo: social,
            social,
          }
        : null),
      seo: {
        title: `Vacature: ${vacancy.title}`,
        description: 'Wil jij werk hebben dat betekenis heeft? Werken in een dynamisch, innovatief en christelijk mediabedrijf? Solliciteer bij de EO!',
      },
      ...dynamicInputs,
    }
  },
  data () {
    return {
      id: '',
      slug: '',
      location: {},
      minHours: '',
      maxHours: '',
      title: '',
      description: '',
      requirements: '',
      department: '',
      createdAt: '',
      employementType: '',
      category: '',
      experience: '',
      education: '',
      tags: [],
      dynamicFields: [],
      hero: this.$config.heros['werkenbij-vacatures'],
      inputName: null,
      inputEmail: null,
      inputPhone: null,
      inputCoverLetter: null,
      inputPortraitPhoto: null,
      inputPortraitPhotoFile: null,
      inputCV: null,
      inputCVFile: null,
      questionResponses: [],
      submitStatus: null,
      recruiter: null,
    }
  },
  computed: {
    errorMessages () {
      const dynamicFields = {}
      if (this.dynamicFields) {
        this.dynamicFields.forEach((f) => {
          dynamicFields[`input${f.id}`] = ['dit is een verplicht veld.']
        })
      }

      return {
        inputName: ['naam is een verplicht veld.'],
        inputEmail: ['vul een correct e-mailadres in.'],
        inputPhone: ['vul een correct telefoonnummer in.'],
        inputCV: ['upload je recent bijgewerkte CV.'],
        inputCoverLetter: ['we horen graag je motivatie.'],
        inputPortraitPhoto: ['het bestand voldoet niet aan de vereisten.'],
        ...dynamicFields,
      }
    },
    hours () {
      let hours = ''
      if (this.minHours) {
        hours = this.minHours
      }
      if (this.maxHours && this.maxHours !== this.minHours) {
        if (hours) {
          hours += ' uur - '
        }
        hours += this.maxHours
      }
      if (hours) {
        hours += ' uur'
      }
      return hours
    },
  },
  validations () {
    const dynamicInputs = {}

    if (this.dynamicFields) {
      this.dynamicFields.forEach((f) => {
        dynamicInputs[`input${f.id}`] = {}
        if (f.required) {
          dynamicInputs[`input${f.id}`].required = required
        }
      })
    }

    return {
      inputName: {
        required,
        minLength: minLength(2),
      },
      inputEmail: {
        required,
        email,
      },
      inputPhone: {
        required,
        validPhone:
          value => /^((\+|00(\s|\s?-\s?)?)31(\s|\s?-\s?)?(\(0\)[-\s]?)?|0)[1-9]((\s|\s?-\s?)?[0-9])((\s|\s?-\s?)?[0-9])((\s|\s?-\s?)?[0-9])\s?[0-9]\s?[0-9]\s?[0-9]\s?[0-9]\s?[0-9]$/.test(value) ||
          value === null ||
          value === '',
      },
      inputCoverLetter: {
        required: this.coverLetter === 'required' && required,
      },
      inputPortraitPhoto: {
        required: this.portraitPhoto === 'required' && required,
      },
      inputCV: {
        required,
      },
      ...dynamicInputs,
    }
  },
  methods: {
    submit (e) {
      e.preventDefault()
      this.submitStatus = 'PENDING'
      this.$v.$touch()
      if (this.$v.$invalid) {
        this.submitStatus = 'ERROR'
      } else {
        this.submitStatus = 'PENDING'
        this.postForm()
      }
    },
    handleFileUpload (e, name) {
      this[`${name}File`] = e.target.files[0]
    },
    /**
     * postForm
     * post form via mailform endpoint.
     * @returns {Promise<void>}
     */
    async postForm () {
      const formData = new FormData()
      if (this.inputName) {
        formData.append('candidate[name]', this.inputName)
      }
      if (this.inputEmail) {
        formData.append('candidate[email]', this.inputEmail)
      }
      if (this.inputPhone) {
        formData.append('candidate[phone]', this.inputPhone)
      }
      if (this.inputCoverLetter) {
        formData.append('candidate[cover_letter]', this.inputCoverLetter)
      }
      if (this.inputPortraitPhotoFile) {
        formData.append('candidate[photo]', this.inputPortraitPhotoFile)
      }
      if (this.inputCVFile) {
        formData.append('candidate[cv]', this.inputCVFile)
      }

      if (this.dynamicFields) {
        this.dynamicFields.forEach((f, i) => {
          let answerType = 'content'
          let key = `input${f.id}`

          if (f.kind === 'file') {
            key += 'File'
            answerType = 'file'
          }

          const answer = this[key]

          if (answer === null || answer === undefined) {
            return
          }

          formData.append(`candidate[open_question_answers_attributes][${i}][open_question_id]`, f.id)

          if (f.kind === 'multi_choice') {
            if (Array.isArray(answer)) {
              answer.forEach((a) => {
                formData.append(`candidate[open_question_answers_attributes][${i}][multi_content][]`, a)
              })
            } else if (answer) {
              formData.append(`candidate[open_question_answers_attributes][${i}][multi_content][]`, answer)
            }
          } else if (f.kind === 'boolean' || f.kind === 'legal') {
            formData.append(`candidate[open_question_answers_attributes][${i}][flag]`, `${!!answer}`)
          } else {
            formData.append(`candidate[open_question_answers_attributes][${i}][${answerType}]`, answer)
          }
        })
      }

      try {
        // TEST API: https://eotest.recruitee.com/api
        await axios.post(`https://eo.recruitee.com/api/offers/${this.slug}/candidates`, formData)
        this.submitStatus = 'OK' 
      } catch (error) {
        this.submitStatus = 'ERROR'
        throw new Error(error)
      }
    },
  },
}
